"use client";

import { Typography } from "@/components/ui/typography";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { memo, useState } from "react";
import { cn } from "@/lib/utils";
import ArrowLink from "@/components/ui/arrow-link";

export type FaqItem = {
  id: string;
  title: string;
  description: string | string[];
  linkText?: string;
  link?: string;
};

export type FaqStackProps = {
  items: FaqItem[];
} & React.ComponentProps<"div">;

const FaqStack = ({ items, className }: FaqStackProps) => {
  const [activeAccordion, setActiveAccordion] = useState<string>(items[0]?.id);

  return (
    <Accordion
      type="single"
      collapsible
      value={activeAccordion}
      onValueChange={setActiveAccordion}
      className={cn(
        "grid grid-cols-1 gap-2 divide-y divide-neutral-200 lg:border-gray-100",
        className
      )}
    >
      {items?.map(({ id, title, description, link, linkText }, idx, arr) => {
        const isLast = idx === arr.length - 1;
        return (
          <AccordionItem
            key={id}
            value={id}
            className={cn("!mt-0 !mb-0", {
              "border-b border-neutral-200 lg:border-gray-100": !isLast,
            })}
          >
            <AccordionTrigger className="cursor-pointer font-semibold hover:no-underline flex justify-between lg:!py-3">
              <Typography
                variant="titleSm"
                className="font-semibold lg:text-xl lg:leading-[130%] lg:font-medium"
                asChild
              >
                <h4>{title}</h4>
              </Typography>
            </AccordionTrigger>

            <AccordionContent className="text-semantic-text-primary lg:text-[#666666] text-base leading-[160%] pt-2 lg:pt-0 pb-6 lg:pb-3">
              <div className="space-y-4 leading-[160%] lg:leading-[145%] [&>p>strong]:font-semibold">
                {Array.isArray(description) ? (
                  description.map((paragraph, i) => (
                    <p
                      key={i}
                      dangerouslySetInnerHTML={{
                        __html: paragraph,
                      }}
                    />
                  ))
                ) : (
                  <p
                    dangerouslySetInnerHTML={{
                      __html: description,
                    }}
                  />
                )}
              </div>

              {linkText && link && (
                <div className="mt-2 md:mt-4 flex">
                  <ArrowLink href={link ?? "#"}>{linkText}</ArrowLink>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};

export default memo(FaqStack);
